// Printable trade documents: the invoice and the trade price list, each built
// as a standalone HTML page. Farm OS shows that page as the preview and the
// desktop app prints the very same page to PDF with Chromium (see pdf.js and
// main.js), so what's on screen is what gets printed or emailed.
//
// The price list reproduces the A5 sheet first made in October 2026 (sizes
// and colours measured from that PDF), so a reprint after editing prices
// looks the same as the one already handed out.
import {
  formatGBP, formatDay, quantityLabel, sectionPriceFormat, termsLabel, ymd, lineAmount, invoiceTotal, PAID_METHODS,
} from './trade';

const FONTS_URL = 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400..800&family=DM+Serif+Display&display=swap';

export const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Multi-line text (addresses, notes) with its line breaks kept
const lines = (s) => esc(String(s || '').trim()).replace(/\r?\n/g, '<br>');

// Colours from the printed price list: ink (names), body (sentences), muted
// (descriptions, labels), green (headings, prices), link, light (the "Drop"
// of the wordmark), line (rules between rows) and wash (the notes box).
const BASE_CSS = `
*{box-sizing:border-box;margin:0;padding:0}
html,body{background:#fff}
body{font-family:'DM Sans 9pt','DM Sans',Helvetica,Arial,sans-serif;font-variation-settings:'opsz' 9;color:#111a14;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.wordmark{font-family:'DM Serif Display',Georgia,serif;font-weight:400;color:#1f5a35;line-height:1;white-space:nowrap}
.wordmark span{color:#4f9a68}
`;

// A document is { title, css, body, pageSize, landscape, widthMm, heightMm }.
// docHtml() turns one into the full page used for previews and PDFs.
export function docHtml({ title, css, body }) {
  return `<!DOCTYPE html>
<html lang="en-GB">
<head>
<meta charset="utf-8">
<title>${esc(title)}</title>
<link rel="stylesheet" href="${FONTS_URL}">
<style>${BASE_CSS}${css}</style>
</head>
<body>${body}</body>
</html>`;
}

// ── Price list (A5, or two to an A4 sheet for printing and cutting) ──────────

const PRICE_LIST_CSS = `
.sheet{width:420pt;height:594pt;padding:38px 42px 0;display:flex;flex-direction:column;overflow:hidden;background:#fff}
.pair{display:flex;width:841pt;height:594pt}
.pair .sheet{width:420.5pt}
.pl-head{display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:11.7px;border-bottom:1px solid #2f7a4a}
.pl-head .wordmark{font-size:33.33px}
.pl-title{text-align:right;padding-bottom:3px}
.pl-kicker{font-size:12px;font-weight:700;letter-spacing:.034em;text-transform:uppercase;line-height:1}
.pl-from{font-size:10.13px;color:#5e7265;margin-top:5px;line-height:1}
.pl-intro{margin-top:11.7px;font-size:10.93px;line-height:16px;color:#2a3b32}
.pl-section{margin-top:14.3px}
.pl-section-head{display:flex;justify-content:space-between;align-items:baseline}
.pl-section-head h2{font-family:'DM Serif Display',Georgia,serif;font-weight:400;font-size:17.33px;line-height:1.2;color:#1f5a35}
.pl-section-head span{font-size:9.33px;font-weight:500;color:#5e7265}
.pl-section table{width:100%;border-collapse:collapse;margin-top:2.2px}
.pl-section td{padding:5.0px 0;vertical-align:middle;border-bottom:1px solid #d4e0d4}
.pl-section tr:last-child td{border-bottom:0}
.pl-section .n{width:161px;font-size:12px;font-weight:700;padding-right:8px}
.pl-section .d{font-size:9.85px;color:#5e7265;padding-right:8px;padding-bottom:7px}
.pl-section .p{width:57px;text-align:right;font-size:12.8px;font-weight:700;color:#1f5a35;white-space:nowrap}
.pl-foot{margin-top:auto;padding-bottom:29.3px}
.pl-terms{background:#eef5ef;border-radius:8px;padding:12px 14px 12px 27px;font-size:10px;line-height:16px;color:#2a3b32}
.pl-contact{display:flex;justify-content:space-between;align-items:flex-end;margin-top:11px;font-size:10.93px;line-height:16.5px;color:#2a3b32}
.pl-contact strong{display:block;font-size:12.27px;color:#111a14}
.pl-web{font-weight:700;color:#2f7a4a}
`;

function priceSheet(pl) {
  const sections = (pl.sections || []).map((s) => {
    const price = sectionPriceFormat(s.items);
    const rows = (s.items || []).map(it => `
      <tr><td class="n">${esc(it.name)}</td><td class="d">${esc(it.description)}</td><td class="p">${esc(price(it.price))}</td></tr>`).join('');
    return `
    <section class="pl-section">
      <div class="pl-section-head"><h2>${esc(s.name)}</h2>${s.unit ? `<span>per ${esc(s.unit)}</span>` : ''}</div>
      <table>${rows}</table>
    </section>`;
  }).join('');

  const terms = (pl.terms || []).filter(t => String(t).trim());
  const contactLines = [pl.phone, pl.email].filter(Boolean).map(v => `<div>${esc(v)}</div>`).join('');

  return `
  <div class="sheet">
    <header class="pl-head">
      <div class="wordmark">Tear<span>Drop</span></div>
      <div class="pl-title">
        <div class="pl-kicker">Trade price list</div>
        ${pl.effectiveFrom ? `<div class="pl-from">${esc(pl.effectiveFrom)}</div>` : ''}
      </div>
    </header>
    ${pl.intro ? `<p class="pl-intro">${esc(pl.intro)}</p>` : ''}
    ${sections}
    <div class="pl-foot">
      ${terms.length ? `<ul class="pl-terms">${terms.map(t => `<li>${esc(t)}</li>`).join('')}</ul>` : ''}
      <div class="pl-contact">
        <div>${pl.contactName ? `<strong>${esc(pl.contactName)}</strong>` : ''}${contactLines}</div>
        ${pl.website ? `<div class="pl-web">${esc(pl.website)}</div>` : ''}
      </div>
    </div>
  </div>`;
}

export function priceListDoc(priceList, { twoUp = false } = {}) {
  const sheet = priceSheet(priceList || {});
  return {
    title: 'TearDrop trade price list',
    css: `@page{size:${twoUp ? 'A4 landscape' : 'A5'};margin:0}${PRICE_LIST_CSS}`,
    body: twoUp ? `<div class="pair">${sheet}${sheet}</div>` : sheet,
    pageSize: twoUp ? 'A4' : 'A5',
    landscape: twoUp,
    widthMm: twoUp ? 297 : 148.2, // the sheet is 420pt, a hair over 148mm
    heightMm: 210,
  };
}

// ── Invoice (A4) ──────────────────────────────────────────────────────────────

const INVOICE_CSS = `
@page{size:A4;margin:15mm 16mm 12mm}
.page{width:210mm;min-height:297mm;padding:15mm 16mm 12mm;display:flex;flex-direction:column;font-size:11.5px;line-height:1.55;color:#2a3b32;background:#fff}
.inv-head{display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:12px;border-bottom:1px solid #2f7a4a}
.inv-head .wordmark{font-size:40px}
.inv-title{text-align:right;line-height:1.2}
.inv-kicker{font-size:15px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#111a14}
.inv-number{font-size:12px;color:#5e7265;margin-top:2px}
.stamp{display:inline-block;margin-top:6px;padding:2px 9px;border:1.5px solid currentColor;border-radius:4px;font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase}
.stamp.paid{color:#1f5a35}
.stamp.void{color:#b42318}
.parties{display:grid;grid-template-columns:1fr 1fr auto;gap:22px;margin:20px 0 24px}
.cap{display:block;font-size:9px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#5e7265;margin-bottom:4px}
.party strong{display:block;font-size:12.5px;color:#111a14}
.meta{border-collapse:collapse;align-self:start}
.meta th{text-align:left;font-weight:400;color:#5e7265;padding:0 18px 3px 0;white-space:nowrap}
.meta td{text-align:right;font-weight:600;color:#111a14;padding:0 0 3px;white-space:nowrap}
.lines{width:100%;border-collapse:collapse;font-size:12px}
.lines thead{display:table-header-group}
.lines th{font-size:9px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#5e7265;text-align:left;padding:0 10px 7px 0;border-bottom:1px solid #2f7a4a}
.lines td{padding:8px 10px 8px 0;border-bottom:1px solid #d4e0d4;vertical-align:top}
.lines tr{break-inside:avoid}
.lines .item{font-weight:700;color:#111a14}
.lines .when{color:#5e7265;white-space:nowrap;width:1%}
.lines .num{text-align:right;white-space:nowrap;width:1%;padding-left:14px}
.lines th.num{padding-left:14px}
.lines .last{padding-right:0}
.lines .amount{font-weight:700;color:#1f5a35}
.lines .empty{color:#5e7265;font-style:italic;text-align:center;padding:18px 0}
.totals{display:flex;flex-direction:column;align-items:flex-end;margin-top:12px;break-inside:avoid}
.total-row{display:flex;align-items:baseline;gap:22px}
.total-row span{font-size:9px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#5e7265}
.total-row strong{font-size:22px;font-weight:700;color:#1f5a35;font-variant-numeric:tabular-nums}
.vat{margin-top:2px;font-size:10px;color:#5e7265}
.pay{margin-top:22px;background:#eef5ef;border-radius:8px;padding:13px 18px 14px;break-inside:avoid}
.pay p{color:#2a3b32}
.pay dl{display:grid;grid-template-columns:auto 1fr;gap:2px 18px;margin-top:8px}
.pay dt{color:#5e7265}
.pay dd{font-weight:600;color:#111a14;font-variant-numeric:tabular-nums}
.notes{margin-top:18px;break-inside:avoid}
.thanks{margin-top:22px;font-family:'DM Serif Display',Georgia,serif;font-size:17px;color:#1f5a35}
.inv-foot{margin-top:auto;padding-top:22px;font-size:9.5px;line-height:1.6;color:#5e7265;text-align:center}
.inv-foot div{padding-top:9px;border-top:1px solid #d4e0d4}
/* On paper the page margins come from @page, so every page gets them */
@media print{.page{width:auto;min-height:calc(270mm - 1px);padding:0}}
`;

const METHOD_LABEL = Object.fromEntries(PAID_METHODS.map(m => [m.key, m.label.toLowerCase()]));

// invoice: { number, status, billTo, issueDate, dueDate, items, notes, paidAt,
// paidMethod }; line amounts and the total are worked out here when missing
// (an unsaved draft in the editor).
export function invoiceDoc(invoice, settings = {}) {
  const inv = invoice || {};
  const s = settings || {};
  const items = (inv.items || []).map(it => ({ ...it, amount: it.amount ?? lineAmount(it.quantity, it.unitPrice) }));
  const total = inv.total ?? invoiceTotal(items);
  const number = inv.number || 'Not numbered yet';
  const bill = inv.billTo || {};

  // One delivery date for every line goes up in the details; a mix of dates
  // gets a date column instead.
  const dates = [...new Set(items.map(it => ymd(it.date)).filter(Boolean))];
  const dateColumn = dates.length > 1 || (dates.length === 1 && items.some(it => !ymd(it.date)));
  const deliveredOn = !dateColumn && dates.length === 1 ? dates[0] : null;

  const from = [
    s.companyName && `<strong>${esc(s.companyName)}</strong>`,
    s.address && `<div>${lines(s.address)}</div>`,
    s.phone && `<div>${esc(s.phone)}</div>`,
    s.email && `<div>${esc(s.email)}</div>`,
    s.website && `<div>${esc(s.website)}</div>`,
  ].filter(Boolean).join('');

  const billLines = [
    `<strong>${esc(bill.businessName || 'Choose a business')}</strong>`,
    bill.contactName && `<div>${esc(bill.contactName)}</div>`,
    bill.line1 && `<div>${esc(bill.line1)}</div>`,
    bill.line2 && `<div>${esc(bill.line2)}</div>`,
    (bill.city || bill.postcode) && `<div>${esc([bill.city, bill.postcode].filter(Boolean).join(' '))}</div>`,
  ].filter(Boolean).join('');

  const meta = [
    ['Invoice date', formatDay(inv.issueDate)],
    ['Due date', formatDay(inv.dueDate)],
    deliveredOn && ['Delivered', formatDay(deliveredOn)],
    inv.issueDate && inv.dueDate && ['Terms', termsLabel(inv.issueDate, inv.dueDate)],
  ].filter(Boolean).map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('');

  const head = `<tr>${dateColumn ? '<th>Date</th>' : ''}<th>Item</th><th class="num">Qty</th><th class="num">Price</th><th class="num last">Amount</th></tr>`;
  const rows = items.length ? items.map(it => `
      <tr>
        ${dateColumn ? `<td class="when">${esc(formatDay(it.date))}</td>` : ''}
        <td class="item">${esc(it.description)}</td>
        <td class="num">${esc(quantityLabel(it.quantity, it.unit))}</td>
        <td class="num">${esc(formatGBP(it.unitPrice))}</td>
        <td class="num last amount">${esc(formatGBP(it.amount))}</td>
      </tr>`).join('')
    : `<tr><td class="empty" colspan="${dateColumn ? 5 : 4}">No lines yet</td></tr>`;

  const hasBank = s.sortCode && s.accountNumber;
  let pay;
  if (inv.status === 'paid') {
    const how = METHOD_LABEL[inv.paidMethod];
    pay = `<p>Paid on ${esc(formatDay(inv.paidAt, { long: true }))}${how ? ` by ${esc(how)}` : ''}. Thank you.</p>`;
  } else if (hasBank) {
    pay = `<span class="cap">Payment</span>
      <p>Please pay by bank transfer by ${esc(formatDay(inv.dueDate, { long: true }))}, quoting the invoice number.</p>
      <dl>
        ${s.accountName ? `<dt>Account name</dt><dd>${esc(s.accountName)}</dd>` : ''}
        <dt>Sort code</dt><dd>${esc(s.sortCode)}</dd>
        <dt>Account number</dt><dd>${esc(s.accountNumber)}</dd>
        <dt>Reference</dt><dd>${esc(number)}</dd>
      </dl>`;
  } else {
    pay = `<span class="cap">Payment</span><p>Payment due by ${esc(formatDay(inv.dueDate, { long: true }))}.</p>`;
  }

  // What a limited company has to show on its invoices, as far as it's been filled in
  const legal = [
    s.companyName && `${esc(s.companyName)}${s.tradingName ? `, trading as ${esc(s.tradingName)}` : ''}.`,
    s.companyNumber && `Registered in England and Wales, company number ${esc(s.companyNumber)}.`,
    s.registeredOffice && `Registered office: ${esc(String(s.registeredOffice).replace(/\s*\n\s*/g, ', '))}.`,
  ].filter(Boolean).join(' ');

  const stamp = inv.status === 'paid' ? '<div class="stamp paid">Paid</div>'
    : inv.status === 'void' ? '<div class="stamp void">Void</div>' : '';

  const body = `
  <div class="page">
    <header class="inv-head">
      <div class="wordmark">Tear<span>Drop</span></div>
      <div class="inv-title">
        <div class="inv-kicker">Invoice</div>
        <div class="inv-number">${esc(number)}</div>
        ${stamp}
      </div>
    </header>
    <section class="parties">
      <div class="party"><span class="cap">From</span>${from}</div>
      <div class="party"><span class="cap">Bill to</span>${billLines}</div>
      <table class="meta">${meta}</table>
    </section>
    <table class="lines">
      <thead>${head}</thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="totals">
      <div class="total-row"><span>${inv.status === 'paid' ? 'Total paid' : 'Total due'}</span><strong>${esc(formatGBP(total))}</strong></div>
      ${s.vatNote ? `<div class="vat">${esc(s.vatNote)}</div>` : ''}
    </div>
    <div class="pay">${pay}</div>
    ${inv.notes ? `<div class="notes"><span class="cap">Notes</span>${lines(inv.notes)}</div>` : ''}
    ${s.footerNote ? `<div class="thanks">${esc(s.footerNote)}</div>` : ''}
    ${legal ? `<footer class="inv-foot"><div>${legal}</div></footer>` : ''}
  </div>`;

  return {
    title: `Invoice ${number}`,
    css: INVOICE_CSS,
    body,
    pageSize: 'A4',
    landscape: false,
    widthMm: 210,
    heightMm: 297,
  };
}
